import { location, reactive } from 'cdui-js';
import { fetchAccounts } from '../api/account';
import { fetchUserInfo } from '../api/login';
import { logout } from '../api/login';
import { getEntryQueryParams, snapshotEntryQuery } from '../lib/entryQuery';
import { redirectForRuntimeLane } from '../lib/runtimeLaneRedirect';
import { showToast } from '../pages/mobile/components/Toast';
import { SSEClient } from '../lib/sse';

/** 客户端来源（与后端 userType / GUEST 无关） */
export type ClientType = 'app' | 'h5' | 'h5_channel';

export type ChannelParams = {
  channelCode: string;
  aeCode: string;
  operatorId: string;
  login: string;
  sys: string;
};

const CHANNEL_H5_STORAGE_KEY = 'difi_channel_h5';
const INVITE_H5_STORAGE_KEY = 'difi_invite_h5';
/** 同 tab 邀请入口只清一次 localStorage，避免登录后带参刷新再次退出 */
const INVITE_LS_CLEARED_KEY = 'difi_invite_ls_cleared';

export type InviteParams = {
  inviteCode: string;
  login: string;
  sys: string;
};

const emptyChannel = (): ChannelParams => ({
  channelCode: '',
  aeCode: '',
  operatorId: '',
  login: '',
  sys: '',
});

const emptyInvite = (): InviteParams => ({
  inviteCode: '',
  login: '',
  sys: '',
});

export const userState = reactive({
  loading: true,
  isGuest: false,
  isGuestLogin: false,
  /** 客户端来源：app | h5 | h5_channel（渠道临时会话） */
  clientType: 'h5' as ClientType,
  /** 渠道入口参数（h5_channel 时有值；也可仅部分有值供登录接口透传） */
  channel: emptyChannel(),
  /** 邀请入口参数（?inviteCode= 或 ?type=INVITE&ch=）；注册时透传 inviteCode */
  invite: emptyInvite(),
  applyStatus: '',
  isLogin: false,
  showLoginBar: false,
  showLoginPage: false,
  accounts: [],
  currentAccount: null,
  accountNumHK: '',
  userInfo: null, //用户信息
  userName: '', //用户名
  countryCode: '', //国家代码
  deviceCode: null,
  email: null, //邮箱
  errorCount: 0, //错误次数
  icon: null, //头像
  id: '', //ID
  ips: '', //IP地址
  lockUntil: '0', //锁定时间
  nickName: null, //昵称
  note: null, //备注
  phone: '', //手机号
  quadraticVerifyLoginOpen: 0, //二次验证登录开关
  secretKey: null, //密钥
  sessionId: null, //会话ID
  smsNotifyOpen: 0, //短信通知开关
  status: 0, //状态
  safePassword: null, // 交易密码
  isDev: true, //是否开发测试
  deviceInfo: null, //app端设备信息

  unreadMessage: false, //未读消息
});

export const isAppUser = () => userState.clientType === 'app';
export const isH5User = () => userState.clientType === 'h5' || userState.clientType === 'h5_channel';
export const isChannelH5User = () => userState.clientType === 'h5_channel';

/** 渠道 / 邀请 H5：仅展示手机 Tab（短信 + 密码），隐藏邮箱/财富号 */
export const isChannelPhoneOnly = () =>
  isChannelH5User() ||
  (userState.channel.login === 'phone' && userState.channel.sys === 'h5') ||
  (userState.invite.login === 'phone' && userState.invite.sys === 'h5' && !!userState.invite.inviteCode);

/** 登录/注册请求透传的渠道字段 */
export const getChannelParams = (): {
  channelCode?: string;
  aeCode?: string;
  operatorId?: string;
} => {
  const { channelCode, aeCode, operatorId } = userState.channel;
  return {
    ...(channelCode ? { channelCode } : {}),
    ...(aeCode ? { aeCode } : {}),
    ...(operatorId ? { operatorId } : {}),
  };
};

/** 注册透传的邀请码（URL / session / 登录页手填均可） */
export const getInviteCode = (): string => {
  const code = userState.invite.inviteCode?.trim();
  return code || '';
};

const queryStr = (key: string) => {
  const q = location.query as Record<string, unknown>;
  if (q[key] != null && String(q[key]) !== '') return String(q[key]);
  return getEntryQueryParams()[key] || '';
};

const readQueryChannel = (): ChannelParams => ({
  channelCode: queryStr('channelCode'),
  aeCode: queryStr('aeCode'),
  operatorId: queryStr('operatorId'),
  login: queryStr('login'),
  sys: queryStr('sys'),
});

/**
 * 邀请码来源：
 * - ?inviteCode=
 * - ?type=INVITE&ch=  （ch 即邀请码，落到登录页下方输入框）
 * type=INVITE 且未带 login/sys 时，按邀请落地页处理：仅手机登录。
 */
const readQueryInvite = (): InviteParams => {
  const type = queryStr('type').toUpperCase();
  const inviteCode = queryStr('inviteCode') || (type === 'INVITE' ? queryStr('ch') : '');
  const typeInvite = type === 'INVITE' && !!inviteCode;
  return {
    inviteCode,
    login: queryStr('login') || (typeInvite ? 'phone' : ''),
    sys: queryStr('sys') || (typeInvite ? 'h5' : ''),
  };
};

const isFullChannelEntry = (c: ChannelParams) =>
  !!(c.channelCode && c.aeCode && c.operatorId && c.login === 'phone' && c.sys === 'h5');

/** 邀请入口：inviteCode + login=phone + sys=h5 */
const isInviteEntry = (i: InviteParams) =>
  !!(i.inviteCode && i.login === 'phone' && i.sys === 'h5');

const persistChannelSession = (c: ChannelParams) => {
  try {
    sessionStorage.setItem(CHANNEL_H5_STORAGE_KEY, JSON.stringify(c));
  } catch {
    /* ignore */
  }
};

const restoreChannelSession = (): ChannelParams | null => {
  try {
    const raw = sessionStorage.getItem(CHANNEL_H5_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ChannelParams>;
    return {
      channelCode: parsed.channelCode ? String(parsed.channelCode) : '',
      aeCode: parsed.aeCode ? String(parsed.aeCode) : '',
      operatorId: parsed.operatorId ? String(parsed.operatorId) : '',
      login: parsed.login ? String(parsed.login) : '',
      sys: parsed.sys ? String(parsed.sys) : '',
    };
  } catch {
    return null;
  }
};

const persistInviteSession = (i: InviteParams) => {
  try {
    sessionStorage.setItem(INVITE_H5_STORAGE_KEY, JSON.stringify(i));
  } catch {
    /* ignore */
  }
};

const restoreInviteSession = (): InviteParams | null => {
  try {
    const raw = sessionStorage.getItem(INVITE_H5_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<InviteParams>;
    return {
      inviteCode: parsed.inviteCode ? String(parsed.inviteCode) : '',
      login: parsed.login ? String(parsed.login) : '',
      sys: parsed.sys ? String(parsed.sys) : '',
    };
  } catch {
    return null;
  }
};

/**
 * App 启动最早调用：判定 APP / 普通 H5 / 渠道 H5 / 邀请 H5。
 * 优先级：APP WebView > 渠道五参齐全 > 邀请三参齐全 > 普通 H5。
 * 渠道/邀请参数写入 sessionStorage，避免后续 routeTo 丢失 query 后读不到。
 */
export const initUserClientType = () => {
  if ((window as any).ReactNativeWebView) {
    userState.clientType = 'app';
    userState.channel = emptyChannel();
    userState.invite = emptyInvite();
    return;
  }

  // 先于渠道/邀请判定快照整份地址栏参数，供 /user/login 原样透传
  snapshotEntryQuery();

  let channel = readQueryChannel();
  if (!isFullChannelEntry(channel)) {
    const cached = restoreChannelSession();
    if (cached && isFullChannelEntry(cached)) {
      channel = cached;
    }
  }

  // 邀请链接从 URL 进入：同会话只清一次 localStorage（避免登录后带参刷新再退出）
  const inviteFromQuery = readQueryInvite();
  if (isInviteEntry(inviteFromQuery)) {
    try {
      if (!sessionStorage.getItem(INVITE_LS_CLEARED_KEY)) {
        localStorage.clear();
        sessionStorage.setItem(INVITE_LS_CLEARED_KEY, '1');
        userState.isLogin = false;
        userState.icon = '';
      }
    } catch {
      /* ignore */
    }
  }

  let invite = inviteFromQuery;
  if (!isInviteEntry(invite)) {
    const cachedInvite = restoreInviteSession();
    if (invite.inviteCode) {
      invite = {
        inviteCode: invite.inviteCode,
        login: invite.login || cachedInvite?.login || '',
        sys: invite.sys || cachedInvite?.sys || '',
      };
    } else if (cachedInvite?.inviteCode) {
      invite = cachedInvite;
    } else {
      invite = emptyInvite();
    }
  }

  userState.channel = channel;
  userState.invite = invite.inviteCode ? invite : emptyInvite();

  if (isFullChannelEntry(channel)) {
    userState.clientType = 'h5_channel';
    persistChannelSession(channel);
    if (invite.inviteCode) persistInviteSession(invite);
    return;
  }

  if (invite.inviteCode) {
    if (isInviteEntry(invite)) {
      // 保证 phoneOnly：把 login/sys 同步到 channel（与渠道判定共用）
      if (!userState.channel.login) userState.channel.login = invite.login;
      if (!userState.channel.sys) userState.channel.sys = invite.sys;
    }
    persistInviteSession(invite);
  }

  userState.clientType = 'h5';
};

export const isShowLoginBar = () => {
  return !userState.isLogin && userState.showLoginBar;
};

export const initAccountInfos = () => {
  return new Promise((resolve, reject) => {
    try {
      fetchAccounts().then((data: any) => {
        const accounts = data;
        let currentAccount = {} as any;
        if (data) {
          currentAccount = data[0];
          userState.accountNumHK = currentAccount.account?.[0]?.accountNum;
        }
        resolve({
          accounts,
          currentAccount,
          accountNumHK: currentAccount.account?.[0]?.accountNum,
        });
      });
    } catch (error) {
      resolve(error);
      console.error(error);
    }
  });
};

/**
 * 将 /user/info（或 App Theme 下发的 userInfo）写入 userState。
 * 只落资料，禁止在这里按 applyStatus 跳开户：活动/奖励等独立页也会走这里，
 * 开户门禁只允许主站 SPA（App bootstrap / 登录成功）显式调用。
 */
export const applyUserInfo = (res: any) => {
  if (!res || typeof res !== 'object') {
    userState.loading = false;
    return;
  }

  userState.userInfo = res;
  userState.applyStatus = res.applyStatus ?? '';
  if (res.userMsg) {
    const data = res.userMsg;
    userState.isGuest = data.userType === 'GUEST';

    console.log(userState.isGuest, 'userState.isGuest');
    userState.countryCode = data.countryCode;
    userState.deviceCode = data.deviceCode;
    userState.email = data.email;
    userState.errorCount = data.errorCount;
    userState.icon = data.icon;
    userState.id = data.id;
    userState.ips = data.ips;
    userState.lockUntil = data.lockUntil;
    userState.nickName = data.nickName;
    userState.note = data.note;
    userState.phone = data.phone;
    userState.quadraticVerifyLoginOpen = data.quadraticVerifyLoginOpen;
    userState.secretKey = data.secretKey;
    userState.sessionId = data.sessionId;
    userState.smsNotifyOpen = data.smsNotifyOpen;
    userState.status = data.status;
    userState.userName = data.username;
    userState.safePassword = data.safePassword;
  }
  userState.loading = false;

  // 纯 H5：按 runtimeLane 整页切域；App WebView 由 Native overlay
  if (!isAppUser()) {
    redirectForRuntimeLane(res);
  }
};

/** 全局单飞：并发共用同一条 /user/info；结束后清空以便登录后可再拉 */
let userInfoLoadPromise: Promise<void> | null = null;

//拉取并存储用户信息到userState（仅 H5；App 走 applyUserInfo）
export const getUserInfo = async () => {
  if (userInfoLoadPromise) return userInfoLoadPromise;
  userInfoLoadPromise = (async () => {
    try {
      const res = await fetchUserInfo();
      applyUserInfo(res);
    } finally {
      userInfoLoadPromise = null;
    }
  })();
  return userInfoLoadPromise;
};

/**
 * 等待全局 userInfo 就绪（业务页用这个，勿直接 getUserInfo）。
 * 已有资料立即返回；否则挂上 bootstrap 已发起的单飞；
 * 尚无 Promise 且有 token 时才触发这一次全局 getUserInfo。
 */
export const awaitUserInfoReady = async () => {
  if (userState.userInfo) return;
  if (userInfoLoadPromise) {
    try {
      await userInfoLoadPromise;
    } catch {
      // 调用方用现有兜底
    }
    return;
  }
  if (!localStorage.getItem('token')) return;
  try {
    await getUserInfo();
  } catch {
    // 调用方用现有兜底（'0' / '--'）
  }
};

export const logoutUser = () => {
  return new Promise(async (resolve) => {
    const data = await logout();
    userState.isLogin = false;
    userState.icon = '';
    userInfoLoadPromise = null;
    localStorage.removeItem('token');
    resolve(data);
  });
};

/**
 * H5 新开户流程最后一步：清本地 token 并打开登录页（不调 logout 接口；APP 不处理）
 */
export const clearH5TokenAfterNewOpen = () => {
  if (!isH5User()) return;
  localStorage.removeItem('token');
  userState.isLogin = false;
  userState.icon = '';
  location.routeTo('/my');
  userState.showLoginPage = true;
};

export const getMessageBySSE = (val) => {
  userState.unreadMessage = val;
};

/**
 * 获取全局消息
 * @returns
 */
export const getUserMessageBySSE = () => {
  let orderSSE = null;
  const sseGetMessageUrl = `${import.meta.env.VITE_BASE_URL}${import.meta.env.VITE_API_BASE_URL}/msg/sse/connect`;
  // const sseGetMessageUrl = `${'http://192.168.0.145:8081'}${import.meta.env.VITE_API_BASE_URL}/msg/sse/connect`;

  // 订阅港股行情更新
  orderSSE = new SSEClient({
    url: sseGetMessageUrl,
    heartbeatTimeout: 20000,
  });

  orderSSE.connect();

  orderSSE.on('connected', (data) => {
    // 连接状态
    console.log(`连接状态connected------------->${JSON.stringify(data)}`);
  });

  //系统通知发布事件
  orderSSE.on('SYS_NOTICE_PUBLISHED ', (payload) => {
    //系统通知发布事件（例如 Redis pub/sub 的系统频道触发刷新）
    console.log('新消息: SYS_NOTICE_PUBLISHED------------->您有新消息了，请注意查收');

    getMessageBySSE(true);
  });

  //业务通知发布事件
  orderSSE.on('BUS_NOTICE_PUBLISHED', (data) => {
    // 业务通知发布事件
    console.log(`站内信新消息到达: BUS_NOTICE_PUBLISHED------------->${JSON.stringify(data)}`);

    getMessageBySSE(true);
  });

  // 站内信/消息中心
  orderSSE.on('STATION_MESSAGE_RECEIVED', (data) => {
    // 站内信/消息中心
    console.log(`站内信/消息中心: STATION_MESSAGE_RECEIVED------------->${JSON.stringify(data)}`);

    getMessageBySSE(true);
  });

  //订阅消息
  orderSSE.on('SUBSCRIBE_MESSAGE_RECEIVED', (data) => {
    // 订阅消息
    console.log(`订阅消息: SUBSCRIBE_MESSAGE_RECEIVED------------->${JSON.stringify(data)}`);

    getMessageBySSE(true);
  });

  //资讯
  orderSSE.on('INFORMATION_MESSAGE_RECEIVED', (data) => {
    // 资讯
    console.log(`资讯: INFORMATION_MESSAGE_RECEIVED------------->${JSON.stringify(data)}`);

    getMessageBySSE(true);
  });

  return orderSSE;
};
